// src/pages/admin/customers/CustomersTab.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  SearchIcon,
  ArrowDown,
  CustomersIcon,
  AccountIcon,
  ProductCatalogIcon,
  CalendarIcon,
  GiftIcon,
  FilterIcon,
  CloseIcon
} from '../../../public/icons';
import AdminLayout from '../layout';
import Pagination from '../../../component/shared/Pagination';
import CustomerDetailsModal from '../../../component/admin/user/CustomerDetailsModal';
import OrderDetailsModal from '../../../component/admin/orders/OrderDetailsModal';   // ← NEW
import adminUserService from '../../../services/admin/admin-usersService';
import adminOrderService from '../../../services/admin/admin-orderService';        // ← NEW
import { useToast } from '../../../context/ToastContext';

const CustomersTab = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [stats, setStats] = useState({
    totalCustomers: 0,
    avgOrdersPerCustomer: 0,
    futureGiftsScheduled: 0
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    itemsPerPage: 10,
    totalPages: 1,
    totalItems: 0
  });

  /* ← NEW: state for the order modal opened from inside the customer modal */
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
  const [orderLoading, setOrderLoading] = useState(false);

  const { success, error: showError } = useToast();

  const statusFilters = [
    { id: 'all', label: 'All status' },
    { id: 'active', label: 'Active' },
    { id: 'inactive', label: 'Inactive' },
    { id: 'suspended', label: 'Suspended' },
    { id: 'pending', label: 'Pending' }
  ];

  /* ────────────── Fetch customers ────────────── */
  const fetchCustomers = useCallback(
    async (page = 1) => {
      try {
        setLoading(true);

        const params = {
          page,
          limit: pagination.itemsPerPage,
          search: searchQuery,
          status: statusFilter !== 'all' ? statusFilter : undefined,
          sort: '-createdAt'
        };

        const response = await adminUserService.getUsers(params);

        if (response.success) {
          setCustomers(response.data.users);
          setPagination((prev) => ({
            ...prev,
            currentPage: response.data.pagination.page,
            totalPages: response.data.pagination.pages,
            totalItems: response.data.pagination.total
          }));
        } else {
          showError(response.message || 'Failed to fetch customers');
          setCustomers([]);
        }
      } catch (error) {
        console.error('Error fetching customers:', error);
        showError('Unable to load customers. Please check your connection.');
        setCustomers([]);
      } finally {
        setLoading(false);
      }
    },
    [searchQuery, statusFilter, pagination.itemsPerPage, showError]
  );

  /* ────────────── Fetch statistics ────────────── */
  const fetchStatistics = useCallback(async () => {
    try {
      const response = await adminUserService.getUserStats();

      if (response.success) {
        const statsData = response.data;
        setStats({
          totalCustomers: statsData.totalUsers || 0,
          avgOrdersPerCustomer:
            statsData.avgOrdersPerCustomer ??
            statsData.avgOrdersPerUser ??
            0,
          futureGiftsScheduled: statsData.futureGiftsScheduled || 0
        });
      }
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  }, []);

  useEffect(() => {
    fetchCustomers(pagination.currentPage);
    fetchStatistics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchCustomers, fetchStatistics]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (pagination.currentPage === 1) {
        fetchCustomers(1);
      } else {
        setPagination((prev) => ({ ...prev, currentPage: 1 }));
      }
    }, 500);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, statusFilter]);

  /* ────────────── Handlers ────────────── */

  const handleViewCustomer = (customer) => {
    setSelectedCustomer(customer);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedCustomer(null);
  };

  /* ← NEW: called from CustomerDetailsModal when "View Order" is clicked */
  const handleViewOrderFromCustomer = useCallback(
    async (orderId) => {
      if (!orderId) {
        console.warn('No orderId passed to handleViewOrderFromCustomer');
        return;
      }
      try {
        setOrderLoading(true);
        const res = await adminOrderService.getOrderById(orderId);

        if (res?.success && res.data?.order) {
          setSelectedOrder(res.data.order);
          setIsOrderModalOpen(true);
        } else if (res?.success && res.data) {
          setSelectedOrder(res.data);
          setIsOrderModalOpen(true);
        } else {
          console.warn('getOrderById returned unexpected shape:', res);
          showError('Failed to load order details');
        }
      } catch (err) {
        console.error('Failed to load order from customer modal:', err);
        showError('Failed to load order details');
      } finally {
        setOrderLoading(false);
      }
    },
    [showError]
  );

  const handleCloseOrderModal = () => {
    setIsOrderModalOpen(false);
    setSelectedOrder(null);
  };

  const handlePageChange = (pageNumber) => {
    setPagination((prev) => ({ ...prev, currentPage: pageNumber }));
    fetchCustomers(pageNumber);
  };

  const handleStatusChange = async (customerId, currentStatus) => {
    try {
      const newStatus = currentStatus === 'active' ? 'suspended' : 'active';
      const response = await adminUserService.updateUserStatus(
        customerId,
        newStatus
      );

      if (response.success) {
        success(`Customer status updated to ${newStatus}`);
        fetchCustomers(pagination.currentPage);
      } else {
        showError(response.message || 'Failed to update status');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      showError('Failed to update status. Please try again.');
    }
  };

  /* ────────────── Formatters ────────────── */

  const getInitials = (firstName, lastName) => {
    const first = firstName?.[0] || '';
    const last = lastName?.[0] || '';
    return `${first}${last}`.toUpperCase() || 'CU';
  };

  const getFutureGiftsBadge = (futureGiftsCount) => {
    const count = futureGiftsCount || 0;
    if (count >= 4) {
      return (
        <span className="px-2 sm:px-3 py-1 sm:py-1.5 text-[10px] sm:text-xs font-normal text-green-600 bg-green-50 rounded-full whitespace-nowrap">
          {count} Scheduled
        </span>
      );
    } else if (count >= 2) {
      return (
        <span className="px-2 sm:px-3 py-1 sm:py-1.5 text-[10px] sm:text-xs font-normal text-blue-600 bg-blue-50 rounded-full whitespace-nowrap">
          {count} Scheduled
        </span>
      );
    } else if (count === 1) {
      return (
        <span className="px-2 sm:px-3 py-1 sm:py-1.5 text-[10px] sm:text-xs font-normal text-yellow-600 bg-yellow-50 rounded-full whitespace-nowrap">
          {count} Scheduled
        </span>
      );
    } else {
      return (
        <span className="px-2 sm:px-3 py-1 sm:py-1.5 text-[10px] sm:text-xs font-normal text-gray-600 bg-gray-50 rounded-full whitespace-nowrap">
          None
        </span>
      );
    }
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-gray-100 text-gray-800',
      suspended: 'bg-red-100 text-red-800',
      pending: 'bg-yellow-100 text-yellow-800'
    };

    return (
      <span
        className={`px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-medium ${
          statusColors[status] || 'bg-gray-100 text-gray-800'
        } whitespace-nowrap`}
      >
        {status}
      </span>
    );
  };

  const displayStats = [
    {
      label: 'Total Customers',
      value: stats.totalCustomers.toString(),
      icon: <AccountIcon />,
      color: 'bg-[#F4E7F1] text-[#8F2F84]'
    },
    {
      label: 'Avg Orders Per Customer',
      value: Math.round(stats.avgOrdersPerCustomer).toString(),
      icon: <ProductCatalogIcon color="#2F578F" />,
      color: 'bg-[#E7ECF4] text-[#2F578F]'
    },
    {
      label: 'Future Gifts Scheduled',
      value: stats.futureGiftsScheduled.toString(),
      icon: <GiftIcon color="#8F7F2F" />,
      color: 'bg-[#F4F2E7] text-[#8F7F2F]'
    }
  ];

  /* ────────────── Mobile Filters ────────────── */
  const MobileFiltersModal = () => (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50"
        onClick={() => setMobileFiltersOpen(false)}
      />
      <div className="fixed bottom-0 left-0 right-0 bg-white rounded-t-2xl z-50 p-5 max-h-[80vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-5">
          <h3 className="text-lg font-medium text-[#1C2A46]">Filters</h3>
          <button
            onClick={() => setMobileFiltersOpen(false)}
            className="p-1"
          >
            <CloseIcon size={20} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search
            </label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                <SearchIcon size={16} className="text-[#6B635D]" />
              </div>
              <input
                type="text"
                placeholder="Search by customer name, email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full p-2.5 pl-10 pr-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#D4A574] text-sm"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <div className="grid grid-cols-2 gap-2">
              {statusFilters.map((filter) => (
                <button
                  key={filter.id}
                  onClick={() => {
                    setStatusFilter(filter.id);
                    setMobileFiltersOpen(false);
                  }}
                  className={`px-3 py-2 text-sm rounded border transition-colors ${
                    statusFilter === filter.id
                      ? 'bg-[#D2A146] text-white border-[#D2A146]'
                      : 'bg-white text-[#1C2A46] border-[#DBDBDB] hover:border-[#D2A146]'
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <button
          onClick={() => setMobileFiltersOpen(false)}
          className="w-full mt-6 py-3 bg-[#D2A146] text-white font-medium rounded hover:bg-[#C49660] transition-colors"
        >
          Apply Filters
        </button>
      </div>
    </>
  );

  return (
    <AdminLayout>
      <div className="px-2 sm:px-4">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6 mb-6 sm:mb-8">
          {displayStats.map((stat, index) => (
            <div
              key={index}
              className="bg-white rounded-sm p-3 sm:p-4 md:p-5 shadow-sm"
            >
              <div className="text-left">
                <div
                  className={`flex justify-center items-center rounded-md h-10 w-10 sm:h-12 sm:w-12 ${stat.color}`}
                >
                  {stat.icon}
                </div>
                <div>
                  <p className="text-xl sm:text-2xl md:text-3xl font-bold text-[#1C2A46] mt-2 sm:mt-4">
                    {stat.value}
                  </p>
                  <p className="text-xs sm:text-sm text-[#6B635D] mt-0.5 sm:mt-1">
                    {stat.label}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-sm shadow-sm">
          {/* All Customers Header */}
          <div className="mb-4 sm:mb-6 p-4 sm:p-6 border-b border-[#DBDBDB]">
            {/* Desktop view */}
            <div className="hidden md:flex items-center justify-between">
              <h2 className="text-lg sm:text-xl font-medium text-[#0E1628]">
                All Customers
              </h2>

              <div className="flex gap-3 lg:gap-4 items-center">
                <div className="relative">
                  <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                    <SearchIcon size={18} className="text-[#6B635D]" />
                  </div>
                  <input
                    type="text"
                    placeholder="Search by customer name, email..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="p-2 pl-9 pr-3 border w-56 lg:w-64 border-[#DBDBDB] bg-transparent placeholder:text-[#6B635D] placeholder:text-xs lg:placeholder:text-sm focus:outline-none focus:ring-1 focus:ring-[#D4A574] focus:border-transparent"
                  />
                </div>

                <div className="relative">
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="p-2 pl-3 pr-7 border w-36 lg:w-40 border-[#DBDBDB] bg-transparent text-[#1C2A46] text-xs lg:text-sm appearance-none focus:outline-none focus:ring-1 focus:ring-[#D4A574] focus:border-transparent"
                  >
                    {statusFilters.map((filter) => (
                      <option key={filter.id} value={filter.id}>
                        {filter.label}
                      </option>
                    ))}
                  </select>
                  <div className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none">
                    <ArrowDown size={14} className="text-[#6B635D]" />
                  </div>
                </div>
              </div>
            </div>

            {/* Mobile view */}
            <div className="md:hidden">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-base font-medium text-[#0E1628]">
                  All Customers
                </h2>
                <button
                  onClick={() => setMobileFiltersOpen(true)}
                  className="flex items-center gap-2 px-3 py-2 border border-[#DBDBDB] rounded-sm text-sm"
                >
                  <FilterIcon size={14} />
                  Filters
                </button>
              </div>

              <div className="relative">
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                  <SearchIcon size={16} className="text-[#6B635D]" />
                </div>
                <input
                  type="text"
                  placeholder="Search by customer name, email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full p-2.5 pl-10 pr-3 border border-[#DBDBDB] bg-transparent placeholder:text-[#6B635D] placeholder:text-xs focus:outline-none focus:ring-1 focus:ring-[#D4A574] focus:border-transparent rounded"
                />
              </div>
            </div>
          </div>

          {/* Loading State */}
          {loading ? (
            <div className="flex justify-center items-center py-12 sm:py-20">
              <div className="text-center">
                <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-[#D2A146] mx-auto"></div>
                <p className="mt-3 sm:mt-4 text-gray-600 text-sm">
                  Loading customers...
                </p>
              </div>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-[700px] md:min-w-full divide-y divide-[#DBDBDB]">
                  <thead className="bg-[#F3F4F6]">
                    <tr>
                      <th className="px-3 sm:px-4 md:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-normal text-[#6B635D] uppercase tracking-wider">
                        Customer
                      </th>
                      <th className="px-3 sm:px-4 md:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-normal text-[#6B635D] uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-3 sm:px-4 md:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-normal text-[#6B635D] uppercase tracking-wider">
                        Orders
                      </th>
                      <th className="px-3 sm:px-4 md:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-normal text-[#6B635D] uppercase tracking-wider">
                        Future Gifts
                      </th>
                      <th className="px-3 sm:px-4 md:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-normal text-[#6B635D] uppercase tracking-wider">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-[#DBDBDB] text-left">
                    {customers.length > 0 ? (
                      customers.map((customer) => (
                        <tr
                          key={customer._id}
                          className="hover:bg-gray-50 transition-colors duration-150"
                        >
                          <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="h-8 w-8 sm:h-10 sm:w-10 flex-shrink-0 bg-gray-200 rounded-full flex items-center justify-center mr-2 sm:mr-3">
                                <span className="text-xs sm:text-sm font-medium text-gray-600">
                                  {getInitials(
                                    customer.firstName,
                                    customer.lastName
                                  )}
                                </span>
                              </div>
                              <div>
                                <span className="text-xs sm:text-sm font-medium text-[#0E1628]">
                                  {customer.firstName} {customer.lastName}
                                </span>
                                <p className="text-[10px] sm:text-xs text-[#8A9099] mt-0.5 sm:mt-1">
                                  Joined{' '}
                                  {new Date(
                                    customer.createdAt
                                  ).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 whitespace-nowrap">
                            <span className="text-xs sm:text-sm text-[#0E1628] break-all">
                              {customer.email}
                            </span>
                          </td>
                          <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <span className="text-xs sm:text-sm font-medium text-[#8A9099]">
                                {customer.totalOrders || 0}
                              </span>
                            </div>
                          </td>
                          <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 whitespace-nowrap">
                            {getFutureGiftsBadge(customer.futureGiftsCount)}
                          </td>
                          <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 whitespace-nowrap">
                            <button
                              onClick={() => handleViewCustomer(customer)}
                              className="text-xs sm:text-sm font-normal text-secondary hover:text-[#C49660] transition-colors"
                            >
                              View Details
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan="6"
                          className="px-4 sm:px-6 py-10 sm:py-12 text-center"
                        >
                          <div className="text-gray-500">
                            <svg
                              className="mx-auto h-10 w-10 sm:h-12 sm:w-12 text-gray-400"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={1.5}
                                d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
                              />
                            </svg>
                            <h3 className="mt-2 text-sm font-medium text-gray-900">
                              No customers found
                            </h3>
                            <p className="mt-1 text-xs sm:text-sm text-gray-500">
                              {searchQuery || statusFilter !== 'all'
                                ? 'Try adjusting your search or filter'
                                : 'No customers in the system yet'}
                            </p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="p-4 sm:p-6 border-t border-[#DBDBDB]">
                  <div className="flex justify-center">
                    <Pagination
                      currentPage={pagination.currentPage}
                      totalPages={pagination.totalPages}
                      totalItems={pagination.totalItems}
                      itemsPerPage={pagination.itemsPerPage}
                      onPageChange={handlePageChange}
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Customer Details Modal — now forwards onViewOrder */}
        {selectedCustomer && (
          <CustomerDetailsModal
            isOpen={isModalOpen}
            onClose={handleCloseModal}
            customer={selectedCustomer}
            onStatusChange={() => fetchCustomers(pagination.currentPage)}
            onViewOrder={handleViewOrderFromCustomer}   // ← NEW
          />
        )}

        {/* Order Details Modal — opens when a customer's order is clicked */}
        {orderLoading && (
          <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black bg-opacity-30">
            <div className="bg-white px-6 py-4 rounded shadow-lg">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#D2A146] mx-auto" />
              <p className="mt-2 text-xs text-gray-600">Loading order…</p>
            </div>
          </div>
        )}

        <OrderDetailsModal
          isOpen={isOrderModalOpen}
          onClose={handleCloseOrderModal}
          order={selectedOrder}
        />

        {/* Mobile Filters Modal */}
        {mobileFiltersOpen && <MobileFiltersModal />}
      </div>
    </AdminLayout>
  );
};

export default CustomersTab;